import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  Paper,
  styled,
  TextField,
  Typography
} from "@mui/material";
import {
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarExport,
  GridToolbarFilterButton,
  GridToolbarQuickFilter
} from "@mui/x-data-grid";
import { Buffer } from 'buffer';
import React, { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useHistory } from "react-router-dom";
import BuyWithAutoComplete from "../admin/manual-buy/BuyWithAutoComplete";
import { setResponse } from "../broker/angelone/websocket2.0";
import ATSnackbar from "../layout/ATSnackbar";
import { useInterval, useTabActive } from "../util/hooks";
import { connectToWS, subscribeToWS } from "./angeloen-websockets";
import WatchListData, { isNumeric } from "./WatchListData";
import AlertDialog from "../util/AlertDialog";
import { alpha, Switch } from "@material-ui/core";
import {green} from "@mui/material/colors";

const isMarketNotOpen = () => {
  const marketStartTime = new Date()
  marketStartTime.setHours(9)
  marketStartTime.setMinutes(15)
  marketStartTime.setSeconds(0)

  const marketEndTime = new Date()
  marketEndTime.setHours(15)
  marketEndTime.setMinutes(30)
  marketEndTime.setSeconds(0)

  const currentTime = new Date()

  const isMarketOpen = marketStartTime < currentTime && marketEndTime > currentTime
  return !isMarketOpen
}

function CustomToolbar() {
  return (
    <GridToolbarContainer title="Data" sx={{ justifyContent: 'flex-start' }}>
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <GridToolbarExport />
      <GridToolbarQuickFilter />
    </GridToolbarContainer>
  );
}

const WatchList = ({ user }) => {
  const history = useHistory();
  const [clientDetails, setClientDetails] = useState()
  const [watchList, setWatchList] = useState([])
  const [watchListLiveMap, setWatchListLiveMap] = useState({})
  const [isLoadingWatchList, setIsLoadingWatchList] = useState(false)
  const [selectedSymbol, setSelectedSymbol] = useState();
  const [warningMessage, setWarningMessage] = useState("")
  const [shortListedTrades, setShortListedTrades] = useState([])
  const [totalPnl, setTotalPnl] = useState(0);
  const [openTrades, setOpenTrades] = useState([])
  const [updateAllGrids, setUpdateAllGrids] = useState(0)
  const [open, setOpen] = useState(false)
  const [isKillSwitchOn, setIsKillSwitchOn] = useState()
  const [webSocket, setWebSocket] = useState(null)
  const { register, formState: { errors } } = useForm()
  const { register: registerDhDefaults, handleSubmit: handleSubmitDhDefaults, reset: resetDhDefaults } = useForm()

  const isTabInactive = !useTabActive();

  const wsMessageBuffer = useRef({});

  // Makes sure we subscribe only initally.
  const isWebSocketSubscribed = useRef(false)

  // Those rows which have shortListed trades, we move them to top.
  // This is required because we are using setWatchList inside useEffect which has watchList dependency.
  // This will avoid infinite rerendering and allow to rerender only when needed e.g., on adding/delete watchlist
  const isWatchListSorted = useRef(false)

  useEffect(() => {
    // We load watchlist only after connecting to websocket
    setIsLoadingWatchList(true)
    const endpoint = '/algo-trading/api/ws/client-details';
    fetch(endpoint, {
      headers: new Headers({
        'Authorization': user?.credential
      })
    })
      .then(response => response.json())
      .then(clientCreds => {
        connectToWS(clientCreds, setWebSocket, onMessage);
        setClientDetails(clientCreds)
        setIsLoadingWatchList(false)
      }).catch(e => history.push('/'));

    fetchDhDefaults()
  }, [user]);


  useEffect(() => {
    getWatchList()
    fetchShortListedTrades()
    fetchOpenTrades()
  }, [user, updateAllGrids])

  useEffect(() => {
    // Don't send websocket data request unless we are connected to websocket and we have watchlist data
    if (webSocket?.readyState !== 1 || watchList?.length === 0 || isWebSocketSubscribed.current)
      return

    const tokens = watchList.map(w => w?.tokenId)
    subscribeToWS(webSocket, tokens)
    isWebSocketSubscribed.current = true
  }, [watchList, webSocket])

  useEffect(() => {
    // After selecting a symbol by pressing enter, add it to watchlist 
    if (!selectedSymbol) return
    addToWatchList(selectedSymbol)
  }, [selectedSymbol])

  useEffect(() => {
    // Don't fetch here initially because other useEffect is doing it
    if (updateAllGrids > 0) {
      fetchShortListedTrades()
      fetchOpenTrades()
    }
  }, [updateAllGrids]);


  // If we have 200 watchlist items, we receive 200 messages from websocket
  // Calling setState 200 times is slow, so we created a wsMessageBuffer
  // We set an interval which will flush all data in the buffer to our state
  // so we call setState every 1 second which improves performance
  useInterval(() => {
    if (Object.keys(wsMessageBuffer.current).length === 0)
      return
    setWatchListLiveMap(prev => ({ ...prev, ...wsMessageBuffer.current }))
    wsMessageBuffer.current = {}
  }, 3000)


  // In market time, watchlist data changes automatically
  // Set an interval to fetch new watchlist every 30 sec
  useInterval(() => {
    if (isMarketNotOpen() || isTabInactive)
      return
    webSocket?.close()
    // Passing custom onCloseWebsocket so that it does not do setWebSocket(null)
    connectToWS(clientDetails, setWebSocket, onMessage, onCloseWebsocket);
    getWatchList()
    isWebSocketSubscribed.current = false;
  }, 90000)

  const addToWatchList = async (data) => {
    setIsLoadingWatchList(true)
    const endpoint = `/algo-trading/api/watchList`;
    const request = {};
    request.id = "";
    request.symbol = data?.symbol;
    request.tokenId = data?.token;
    request.userId = user?.email;
    request.userTradeStrategy = "TEST4";

    fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': user.credential
      },
      body: JSON.stringify(request)
    }
    ).then(async (response) => {
      if (response.status >= 400) {
        response.text()
          .then(r => {
            console.error(r)
            setWarningMessage(r)
            setOpen(true)
          })
        return
      }
      const addedWatchList = JSON.parse(await response.text())
      isWatchListSorted.current = false
      setWatchList(prev => [addedWatchList, ...prev])
      subscribeToWS(webSocket, [addedWatchList?.tokenId])
    }).finally(() => setIsLoadingWatchList(false));
  }

  const getWatchList = () => {
    const endpoint = `/algo-trading/api/watchList`;
    fetch(endpoint, {
      headers: new Headers({
        'Authorization': user.credential
      }),
    }
    ).then(response => {
      if (response.status >= 400) {
        response.text()
          .then(r => {
            console.error(r)
            setWarningMessage(r)
            setOpen(true)
          })
        return
      }
      response.text()
        .then(value => {
          const watchListResponse = JSON.parse(value)
          isWatchListSorted.current = false
          setWatchList(watchListResponse)
        })
    }
    ).catch(error => {
      console.log(error);
      history.push('/')
    }).finally(() => setIsLoadingWatchList(false));
  }


  const onMessage = function (event) {
    // console.log(`[message] Data received from server:`, event);
    const message = event.data
    if (message instanceof ArrayBuffer) {
      const receivedData = setResponse(Buffer.from(message), message);
      // console.log('receivedData', receivedData)

      wsMessageBuffer.current[receivedData?.token] = receivedData
    }
    else if (message instanceof Blob) {
      console.log('Blob message: ', message)
    }
  };

  const onCloseWebsocket = (e, setWebSocket) => {
    console.log(`[close] Closing socket connections:`, e);
  }

  const deleteWatchListIemFromUI = id => {
    isWatchListSorted.current = false
    setWatchList(prev => prev?.filter(w => w?.id !== id))
  }

  const fetchShortListedTrades = () => {
    const endpoint = '/algo-trading/api/dh/short-listed-trades-active';
    fetch(endpoint, {
      headers: new Headers({
        'Authorization': user.credential
      })
    })
      .then(response => response.json())
      .then(data => Array.isArray(data) && setShortListedTrades(data));
  }

  const fetchOpenTrades = () => {
    const endpoint = '/algo-trading/api/dh/open-trades';
    fetch(endpoint, {
      headers: new Headers({
        'Authorization': user.credential
      })
    })
      .then(response => response.json())
      .then(data => {
        if (Array.isArray(data))
          setOpenTrades(data)
      });
  }

  const fetchDhDefaults = () => {
    fetch('/algo-trading/api/dh/user-defaults', {
      headers: {
        'Authorization': user?.credential
      }
    })
      .then(response => response.json())
      .then(json => {
        resetDhDefaults(json)
        setIsKillSwitchOn(json?.killSwitch)
      });
  }

  const onSubmitDefaults = data => {
    fetch('/algo-trading/api/dh/user-defaults', {
      headers: {
        'Authorization': user?.credential,
        'Content-Type': 'application/json'
      },
      method: 'POST',
      body: JSON.stringify(data),
    })
      .then(response => response.text())
      .then(response => {
        setWarningMessage(response)
        setOpen(true)
      });
  }

  const totalShortListedTrades = shortListedTrades?.length
  const totalOpenTrades = openTrades?.length
  return (
    <>

      {warningMessage && (<ATSnackbar message={warningMessage} open={open} setOpen={setOpen} severity={'warning'} />)}
      <Grid container item xs={12} md={12} lg={12} gap={1} alignItems='center' >
        <Grid item xs={12} md={6} lg={3} style={{ paddingTop: 20 }}>
          <BuyWithAutoComplete user={user} errors={errors} register={register}
            setSelectedSymbol={setSelectedSymbol} />
        </Grid>
        {totalShortListedTrades >= 0 &&
          <Paper component={Grid} item xs={12} sm={5} md={3} lg={2} style={{ paddingTop: 20 }} alignItems='center'>
            Total Shortlisted: <Typography variant='h4' align='center' display='inline'> {totalShortListedTrades}</Typography>
          </Paper >
        }
        {
          totalOpenTrades >= 0 &&
          <>
            <Paper component={Grid} item xs={12} sm={5} md={3} lg={2} style={{ paddingTop: 20 }} alignItems='center'>
              Total open trades:  <Typography variant='h4' align='center' display='inline'>{totalOpenTrades}</Typography>
            </Paper>
          </>
        }
        {
          isNumeric(totalPnl) &&
          <>
            <Paper component={Grid} item xs={12} sm={5} md={3} lg={2} style={{ paddingTop: 20 }} alignItems='center'>
              Total PnL:  <Typography variant='h4' align='center' display='inline'>{totalPnl?.toFixed(2)}</Typography>
            </Paper>
          </>
        }
      </Grid >
      <Grid item xs={12} md={12}>
        <WatchListData
          customToolBar={CustomToolbar}
          watchList={watchList}
          setWatchList={setWatchList}
          watchListLiveMap={watchListLiveMap}
          setWarningMessage={setWarningMessage}
          setUpdateAllGrids={setUpdateAllGrids}
          shortListedTrades={shortListedTrades}
          setShortListedTrades={setShortListedTrades}
          openTrades={openTrades}
          setOpen={setOpen}
          user={user}
          isLoading={isLoadingWatchList}
          setIsLoadingWatchList={setIsLoadingWatchList}
          deleteWatchListIemFromUI={deleteWatchListIemFromUI}
          setTotalPnl={setTotalPnl}
        />
      </Grid>
      <Grid item lg={12} md={12} sm={12} xs={12}>
        <Box
          component="form"
          onSubmit={handleSubmitDhDefaults(onSubmitDefaults)}
        >
          <Grid container spacing={1} alignItems='center'>
            <Grid item>
              <Typography variant="h6">Defaults:</Typography>
            </Grid>
            <Grid item lg={2} md={3} sm={3} xs={12}>
              <FormControl fullWidth>
                <TextField
                  label='% Stoploss'
                  {...registerDhDefaults('stoploss')}
                  InputProps={{ inputProps: { min: 0, max: 100, step: '.01' }, endAdornment: '%' }}
                  InputLabelProps={{ shrink: true }}
                  type='number'
                  required
                  fullWidth
                />
              </FormControl>
            </Grid>
            <Grid item lg={2} md={3} sm={3} xs={12}>
              <FormControl fullWidth>
                <TextField
                  label='% Trailing stoploss'
                  {...registerDhDefaults('trailingStoploss')}
                  InputProps={{ inputProps: { min: 0, max: 100, step: '.01' }, endAdornment: '%' }}
                  InputLabelProps={{ shrink: true }}
                  type='number'
                  required
                  fullWidth
                />
              </FormControl>
            </Grid>
            <Grid item lg={2} md={3} sm={3} xs={12}>
              <FormControl fullWidth>
                <TextField
                  label='Quantity'
                  {...registerDhDefaults('quantity')}
                  InputProps={{ inputProps: { min: 1 } }}
                  InputLabelProps={{ shrink: true }}
                  type='number'
                  required
                  fullWidth
                />
              </FormControl>
            </Grid>
            <Grid item lg={2} md={3} sm={3} xs={12}>
              <FormControl fullWidth>
                <TextField
                  label='% Trigger price'
                  {...registerDhDefaults('triggerPrice')}
                  InputProps={{ inputProps: { min: 0, max: 100, step: '.01' }, endAdornment: '%' }}
                  InputLabelProps={{ shrink: true }}
                  type='number'
                  required
                  fullWidth
                />
              </FormControl>
            </Grid>
            <Grid item lg={1} md={1} sm={1} xs={12}>
              <Button type='submit' variant="contained">Save</Button>
            </Grid>
          </Grid >
          <Grid item lg={12} md={12} sm={12} xs={12}>
            <KillSwitch
              user={user}
              setWarningMessage={setWarningMessage}
              setOpen={setOpen}
              isKillSwitchOn={isKillSwitchOn}
              setIsKillSwitchOn={setIsKillSwitchOn}
            />
          </Grid>
        </Box>
      </Grid >
    </>
  )
}


const KillSwitch = ({ user, setWarningMessage, setOpen, isKillSwitchOn, setIsKillSwitchOn }) => {
  const [dialogInfo, setDialogInfo] = useState({ open: false, onAgree: null })

  const submitToggleKillSwitch = killSwitchStatus => {
    fetch('/algo-trading/api/dh/user-defaults/kill-switch', {
      headers: {
        'Authorization': user?.credential,
        'Content-Type': 'application/json'
      },
      method: 'PUT',
      body: JSON.stringify({ killSwitch: killSwitchStatus }),
    })
      .then(response => {
        const responseStatus = response?.status
        response?.text()?.then(text => {
          setWarningMessage(text)
          setOpen(true)
          if (responseStatus === 200) {
            setIsKillSwitchOn(killSwitchStatus)
          }
        })
      })
  }

  const onKillSwitchToggle = (event) => {
    const toggleStatus = event?.target?.checked;
    setDialogInfo({
      open: true,
      onAgree: () => submitToggleKillSwitch(toggleStatus)
    })
  }

  const setOpenDialog = open => {
    setDialogInfo(prev => ({
      ...prev,
      open
    }))
  }

  return (
    <>
      <AlertDialog
        open={dialogInfo?.open}
        setOpen={setOpenDialog}
        onAgree={dialogInfo?.onAgree}
        content={`You are about to ${!isKillSwitchOn ? 'enable' : 'disable'} the kill switch. DHAN KILLSWITCH DISABLE IS ONLY ALLOWED ONCE PER DAY`} />

      {(isKillSwitchOn !== undefined && isKillSwitchOn !== null) ?
        <FormGroup style={{ width: 1000, height: 100, marginTop: 20 }}>
          <FormControlLabel
            control={<RedSwitch
              name='killSwitch' checked={isKillSwitchOn} onChange={onKillSwitchToggle}
            />} label="Kill Switch" />
        </FormGroup>
        : <Typography variant="h6" color='error'>Kill switch is not configured</Typography>
      }
    </>
  )
}
export const RedSwitch = styled(Switch)(({ theme }) => ({
  '& .MuiSwitch-switchBase.Mui-checked': {
    color: green[900],
    '&:hover': {
      backgroundColor: alpha(green[900], theme.palette.action.hoverOpacity),
    },
  },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
    backgroundColor: green[900],
  },
}));

export default WatchList